import type { EmailSearch } from "@email/emailSearch";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";

const FRONT_DESK = "/display/front-desk";

/** The front desk keeps its pages inside the display, so shared links resolve against it. */
const useOnFrontDesk = () =>
  useRouterState({
    select: (s) => s.location.pathname.startsWith(FRONT_DESK),
  });

export const useOpenPatient = () => {
  const navigate = useNavigate();
  const onFrontDesk = useOnFrontDesk();
  return useCallback(
    (patientId: string) => {
      if (onFrontDesk) {
        void navigate({
          to: "/display/front-desk/patients/$patientId",
          params: { patientId },
        });
      } else {
        void navigate({ to: "/patients/$patientId", params: { patientId } });
      }
    },
    [navigate, onFrontDesk],
  );
};

export const useOpenCalls = () => {
  const navigate = useNavigate();
  const onFrontDesk = useOnFrontDesk();
  return useCallback(
    () =>
      void navigate({
        to: onFrontDesk ? "/display/front-desk/calls" : "/calls",
      }),
    [navigate, onFrontDesk],
  );
};

export const useOpenCall = () => {
  const navigate = useNavigate();
  const onFrontDesk = useOnFrontDesk();
  return useCallback(
    (callId: string) =>
      void navigate({
        to: onFrontDesk ? "/display/front-desk/calls" : "/calls",
        search: { callId },
      }),
    [navigate, onFrontDesk],
  );
};

export const useOpenEmail = () => {
  const navigate = useNavigate();
  const onFrontDesk = useOnFrontDesk();
  return useCallback(
    (search: EmailSearch = {}, options: { replace?: boolean } = {}) =>
      void navigate({
        to: onFrontDesk ? "/display/front-desk/email" : "/email",
        search,
        replace: options.replace,
      }),
    [navigate, onFrontDesk],
  );
};
